import React, { useMemo, useState, useEffect } from 'react'
import { number, string } from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { Button, IconShareDefault } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import ShareModal from './ShareModalConnected'
import i18nInstance from '../i18n/i18nInstance'
import { trackMobileShareViewed } from '../events'
import { SOURCES } from '../constants'
import { useFetchShareLinks } from '../hooks'

type ShareButtonProps = {
  className?: string | null
  courseSlug: string
  courseId: string | number
  chapterSlug?: string | null
  chapterId?: string | number | null
  title: string
  cinematic12x5?: string | null
  instructorName: string
}

const ShareButton = ({
  className = '',
  courseSlug,
  courseId,
  chapterSlug,
  chapterId,
  title,
  cinematic12x5,
  instructorName,
}: ShareButtonProps) => {
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [isNativeShare, setNativeShare] = useState(false)
  const shareData = useMemo(
    () => ({
      course: { id: courseId, slug: courseSlug, instructorName },
      chapter: { slug: chapterSlug, id: chapterId },
      title,
      modalImage: cinematic12x5,
      source: SOURCES.LESSON_PAGE_LOGGED_OUT,
    }),
    [
      courseId,
      courseSlug,
      chapterId,
      chapterSlug,
      cinematic12x5,
      instructorName,
      title,
    ],
  )
  const { links, loading, onFetchLinks } = useFetchShareLinks(shareData)
  const { t } = useTranslation('@mc/share')

  useEffect(() => {
    const isLessonShare = !!courseId
    if (isEmpty(links) && (isModalOpened || isNativeShare)) {
      onFetchLinks({
        isLessonShare,
        useDeprecated: isNativeShare,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFetchLinks, isModalOpened, isNativeShare, links])

  useEffect(() => {
    // If we're displaying Native Share Sheet, we need to call
    // the endpoint before user clicks the button
    //
    // Disable TS check here ("navigator.share is always defined")
    // for isomorphic support
    //
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window !== 'undefined' && navigator?.share) {
      setNativeShare(true)
    }
  }, [])

  const onClickShare = async () => {
    const trackProperties = {
      source: SOURCES.LESSON_PAGE_LOGGED_OUT,
      page: shareData?.course?.slug,
      lesson: shareData?.chapter?.slug,
    }

    if (isNativeShare && links?.more) {
      try {
        trackMobileShareViewed({ native: true, ...trackProperties })
        await navigator.share({ text: links.more })
      } catch {
        // In case users cancel sharing, we're not doing anything.
      }
    } else {
      setIsModalOpened(true)
    }
  }

  return (
    <>
      {isModalOpened && (
        <ShareModal
          show
          shareData={shareData}
          useDeprecated
          onClose={() => setIsModalOpened(false)}
        />
      )}

      <Button
        kind='secondary'
        className={`mc-bg-gray-200 mc-px-8 ${className}`}
        onClick={onClickShare}
        data-testid='share-button'
        loading={loading}
      >
        <IconShareDefault className='mc-mr-2' />
        {courseId ? t('shareLesson') : t('share')}
      </Button>
    </>
  )
}

ShareButton.propTypes = {
  className: string,
  cinematic12x5: string,
  instructorName: string.isRequired,
  courseSlug: string.isRequired,
  courseId: number.isRequired,
  chapterSlug: string,
  chapterId: number,
  title: string.isRequired,
}

export default withI18nContext(i18nInstance)(ShareButton)
